/**
 * The Sphere AI panel's manual resize grip: a full-height strip on the panel's inner edge (the one
 * facing the page), dragged to set the panel's width.
 *
 * It sits *inside* the panel rather than between the panel and the page so it works in both layout
 * modes with one placement: the overlay panel is `position: fixed` and has no in-flow neighbour to
 * put a divider between. Because it overlays the panel's own edge it stays only a few px wide — the
 * content's 16px gutters mean nothing beneath it is clickable anyway.
 *
 * The grip is a real `<button>`, not a styled `div`: that makes it focusable, so the arrow keys can
 * resize the panel too, and keeps it out of the "interaction handler on a static element" hole.
 *
 * The panel itself doesn't move during the drag — see useChatPanelWidth for why. What follows the
 * pointer is the guide line below.
 */
import type { SidebarPositions } from "$root/components/common/Sidebar-shared";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { createPortal } from "react-dom";
import { chatWidthKeyStep } from "./chatDock";

export function SphereAIPanelResizeHandle({
	position,
	resizing,
	innerRef,
	guideRef,
	onNudge,
	onReset,
}: {
	/** Which side the panel is docked to — the grip goes on the opposite edge. */
	position: SidebarPositions;
	/** Whether a resize is in flight; keeps the grip highlighted for the whole gesture. */
	resizing: boolean;
	/** Receives the grip element, which the drag listeners attach to. */
	innerRef(element: HTMLElement | null): void;
	/** Receives the guide line, which the drag moves imperatively. */
	guideRef: React.RefObject<HTMLDivElement | null>;
	/** Resize by `delta` px (positive = wider), from an arrow key. */
	onNudge(delta: number): void;
	/** Back to the default width, from a double-click. */
	onReset(): void;
}): React.JSX.Element {
	return (
		<>
			<button
				ref={innerRef}
				type="button"
				data-qualifier="SphereAIPanelResizeHandle"
				aria-label="Resize chat panel"
				title="Drag to resize (double-click to reset)"
				onDoubleClick={onReset}
				onKeyDown={(event) => {
					const delta = chatWidthKeyStep(event.key, position);
					if (delta === 0) {
						return;
					}
					// The panel is inside a scrollable page: without this, the same arrow key also scrolls it.
					event.preventDefault();
					onNudge(delta);
				}}
				className={toClassName({
					"absolute inset-y-0 z-10 w-[5px] cursor-col-resize transition-colors": true,
					"right-0": position === "left",
					"left-0": position !== "left",
					// Unstyled at rest — the panel already has a border on this edge, and a second visible
					// line would read as a scrollbar. It shows itself on hover, and holds while dragging even
					// once the pointer has been dragged off the strip.
					"bg-[var(--palette-A500)]": resizing,
					"hover:bg-[var(--palette-N200)]": !resizing,
				})}
			/>
			{/* The guide line: where the panel's edge will land on release. Positioned by `transform`
			    alone, which composites without laying the page out again — the point of deferring the
			    resize in the first place.

			    Portalled to the body rather than into the panel (as floating surfaces here normally are,
			    see SphereAIPanelPortal) for a layout reason, not a styling one: the overlay panel carries
			    a `transform`, which would make it the containing block for a fixed child and reduce the
			    guide to sliding around inside the panel. It inherits nothing it needs — it's one rule. */}
			{createPortal(
				<div
					ref={guideRef}
					aria-hidden
					className="pointer-events-none fixed inset-y-0 left-0 z-[1000] w-0.5 invisible"
					style={{ backgroundColor: themeCSSVars.palette_A500 }}
				/>,
				document.body,
			)}
		</>
	);
}
